// src/diagrams/ContactFlowDiagram.js - Optimized with better performance for large graphs

import React, { useCallback, useEffect, useState, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  MiniMap,
  Background,
  Panel,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { ContactFlowReactFlowParser, NO_SUCCESS_TRANSITION_TYPES } from './contactFlowReactFlowParser';
import { nodeTypes } from './ContactFlowNodes';
import { advancedEdgeTypes, cleanupEdgeTracking } from './routing/AdvancedEdgeComponents';
import { useAdvancedRouting, RoutingPresets } from './routing/useAdvancedRouting';
import { getEstimatedDimensions } from './utils/NodeMeasurement';
import { getLayoutedElements } from './utils/computeFlowLayout';


// Enhanced Contact Flow Diagram Component with Performance Optimizations
const ContactFlowDiagramInner = forwardRef(({
  contactFlow,
  onNodeClick,
  onEdgeClick,
  showMiniMap = true,
  showControls = true,
  fitViewOnLoad = true,
  layoutDirection = 'TB',
  exportedData = null,
  instanceId = null,
  routingConfig = RoutingPresets.BALANCED,
  showAISummary = false,
  // Optional pre-parsed graph (from reactFlowContactFlowService). When provided we
  // reuse it instead of re-parsing contactFlow.Content, avoiding a redundant parse.
  nodes: providedNodes = null,
  edges: providedEdges = null,
  summary: providedSummary = null,
  // Fired once per initialization when the graph is ready for capture (nodes set +
  // edges routed) or when init bails out. Lets batch export await actual readiness
  // instead of a fixed timeout.
  onRenderComplete = null
}, ref) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowSummary, setFlowSummary] = useState(null);
  const lastProcessedFlowRef = useRef(null);
  const routingTimeoutRef = useRef(null);
  
  const { fitView, getViewport, setViewport } = useReactFlow();

  // Resolve the effective routing config. A size-based preset supplies sensible
  // DEFAULTS for the current graph, but the caller's explicit selection
  // (routingConfig, e.g. a user-picked preset) is spread LAST so it always wins —
  // this is what makes the Edge Routing Configuration control actually take effect.
  const optimizedRoutingConfig = useMemo(() => {
    const edgeCount = edges?.length || 0;
    const nodeCount = nodes?.length || 0;

    if (edgeCount > 50 || nodeCount > 30) {
      return { ...RoutingPresets.LARGE_GRAPH, ...routingConfig };
    } else if (edgeCount > 20 || nodeCount > 15) {
      return { ...RoutingPresets.PERFORMANCE, ...routingConfig };
    } else {
      return { ...RoutingPresets.QUALITY, ...routingConfig };
    }
  }, [routingConfig, edges?.length, nodes?.length]);

  // Advanced routing hook with optimized config
  const { 
    processEdgesWithRouting, 
    debouncedProcessEdges, 
    isRouting, 
    routingStats 
  } = useAdvancedRouting(optimizedRoutingConfig);

  // Memoized node validation
  const nodeHasRequiredHandle = useCallback((node, handleId) => {
    if (!node.data) return false;
    
    if (node.data.individualTransitions) {
      return node.data.individualTransitions.some(t => t.handleId === handleId);
    }
    
    const metadata = node.data.metadata;
    if (!metadata) return false;
    
    const handleType = handleId.split('-')[0];
    
    switch (handleType) {
      case 'error':
        return metadata.hasErrors && metadata.errorCount > 0;
      case 'condition':
        return metadata.hasConditions && metadata.conditionCount > 0;
      case 'success':
        // Shared source of truth with the parser so handle validation and transition
        // building can never disagree about which node types emit a success handle.
        return !NO_SUCCESS_TRANSITION_TYPES.includes(node.data.actionType);
      default:
        return false;
    }
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getReactFlowInstance: () => ({
      getNodes: () => nodes,
      getEdges: () => edges,
      getViewport,
      setViewport,
      fitView: () => fitView({
        padding: 0.1,
        duration: 500,
        includeHiddenNodes: true,
        minZoom: 0.05,
        maxZoom: 1
      }),
      getBounds: () => {
        if (nodes.length === 0) return null;
        
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        
        nodes.forEach(node => {
          // Prefer the measured dimensions stored on the node; fall back to the shared estimator
          const est = getEstimatedDimensions(node);
          const nodeWidth = node.width || est.width;
          const nodeHeight = node.height || est.height;

          minX = Math.min(minX, node.position.x);
          minY = Math.min(minY, node.position.y);
          maxX = Math.max(maxX, node.position.x + nodeWidth);
          maxY = Math.max(maxY, node.position.y + nodeHeight);
        });

        return { minX, minY, maxX, maxY };
      }
    }),
    getNodes: () => nodes,
    getEdges: () => edges,
    fitView: () => fitView({
      padding: 0.1,
      duration: 500,
      includeHiddenNodes: true,
      minZoom: 0.05,
      maxZoom: 1
    }),
    getRoutingStats: () => routingStats,
    forceRerender: () => {
      if (nodes.length > 0 && edges.length > 0) {
        // Clear existing timeout
        if (routingTimeoutRef.current) {
          clearTimeout(routingTimeoutRef.current);
        }
        
        // Debounce the routing for performance
        routingTimeoutRef.current = setTimeout(() => {
          debouncedProcessEdges(nodes, edges, layoutDirection).then(routedEdges => {
            setEdges(routedEdges);
          });
        }, 100);
      }
    }
  }), [nodes, edges, getViewport, setViewport, fitView, routingStats, debouncedProcessEdges, layoutDirection, setEdges]);

  // Main initialization effect - optimized to run only once per flow
  useEffect(() => {
    // Skip if we've already initialised this exact flow + layout direction. Keying on
    // Id (not Name) avoids same-name collisions; including layoutDirection lets a
    // direction change re-trigger the layout (the old name-only guard ignored it).
    const flowSignature = `${contactFlow?.Id ?? contactFlow?.Name}-${layoutDirection}`;
    if (lastProcessedFlowRef.current === flowSignature) {
      return;
    }

    // Clean up edge tracking when flow changes
    cleanupEdgeTracking();

    if (!contactFlow || !contactFlow.Content) {
      setError('No contact flow content provided');
      setIsLoading(false);
      onRenderComplete?.();
      return;
    }

    const initializeFlow = async () => {
      try {
        setIsLoading(true);
        setError(null);
        lastProcessedFlowRef.current = flowSignature;

        let parsedNodes;
        let parsedEdges;
        let summary;

        if (providedNodes && providedNodes.length > 0 && providedEdges) {
          // Reuse the graph already parsed by reactFlowContactFlowService. Clone so the
          // layout pass (which mutates position/width/height) cannot corrupt the stored
          // arrays, which would break a later re-selection of the same flow.
          parsedNodes = providedNodes.map(n => ({
            ...n,
            data: { ...n.data },
            position: { ...(n.position || { x: 0, y: 0 }) }
          }));
          parsedEdges = providedEdges.map(e => ({ ...e, data: { ...e.data } }));
          summary = providedSummary || null;
        } else {
          // Fall back to parsing the raw flow content with data lookup.
          const parser = new ContactFlowReactFlowParser(
            contactFlow.Content,
            exportedData,
            instanceId
          );
          const parsed = parser.convertToReactFlow();
          parsedNodes = parsed.nodes;
          parsedEdges = parsed.edges;
          summary = parser.getFlowSummary();
        }

        if (parsedNodes.length === 0) {
          setError('No valid nodes found in contact flow');
          setIsLoading(false);
          onRenderComplete?.();
          return;
        }

        // Apply auto-layout with sizing using accurate measurements
        const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(
          parsedNodes,
          parsedEdges,
          layoutDirection
        );

        // Nodes already have accurate dimensions from measurement-based layout
        const nodesWithDimensions = layoutedNodes;

        // Validate edges
        const validatedEdges = layoutedEdges.map(edge => {
          if (edge.sourceHandle && edge.sourceHandle !== 'success-output') {
            const sourceNode = nodesWithDimensions.find(n => n.id === edge.source);
            if (sourceNode && !nodeHasRequiredHandle(sourceNode, edge.sourceHandle)) {
              return { ...edge, sourceHandle: 'success-output' };
            }
          }
          return edge;
        });

        // Set nodes first
        setNodes(nodesWithDimensions);
        setFlowSummary(summary);
        setIsLoading(false);

        // Process edges with routing after a small delay
        requestAnimationFrame(async () => {
          try {
            const edgesWithRouting = validatedEdges.map(edge => ({
              ...edge,
              data: {
                ...edge.data,
                nodes: nodesWithDimensions,
                layoutDirection,
                routingType: 'advanced',
                routingEnabled: true
              }
            }));

            const routedEdges = await processEdgesWithRouting(
              nodesWithDimensions, 
              edgesWithRouting, 
              layoutDirection
            );
            
            setEdges(routedEdges);

            // Signal readiness on the next frame (after React commits the edges) so
            // batch export can capture as soon as the graph is actually painted.
            requestAnimationFrame(() => onRenderComplete?.());

            // Fit view after edges are processed
            if (fitViewOnLoad) {
              setTimeout(() => {
                fitView({ padding: 0.1, duration: 500 });
              }, 100);
            }
          } catch (error) {
            console.error('Error processing edges:', error);
            setEdges(validatedEdges.map(edge => ({
              ...edge,
              data: {
                ...edge.data,
                nodes: nodesWithDimensions,
                layoutDirection,
                routingType: 'fallback',
                routingEnabled: false
              }
            })));
            // Still ready for capture even though routing fell back.
            requestAnimationFrame(() => onRenderComplete?.());
          }
        });

      } catch (err) {
        console.error('Failed to parse contact flow:', err);
        setError(`Failed to parse contact flow: ${err.message}`);
        setIsLoading(false);
        onRenderComplete?.();
      }
    };

    initializeFlow();
  }, [contactFlow, layoutDirection, fitView, fitViewOnLoad, setNodes, setEdges, exportedData, instanceId, processEdgesWithRouting, nodeHasRequiredHandle, providedNodes, providedEdges, providedSummary, onRenderComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupEdgeTracking();
      if (routingTimeoutRef.current) {
        clearTimeout(routingTimeoutRef.current);
      }
      lastProcessedFlowRef.current = null;
    };
  }, []);

  // Handle connection creation
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Handle node clicks
  const handleNodeClick = useCallback((event, node) => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  }, [onNodeClick]);

  // Handle edge clicks
  const handleEdgeClick = useCallback((event, edge) => {
    if (onEdgeClick) {
      onEdgeClick(edge);
    }
  }, [onEdgeClick]);

  // Re-layout function with optimized routing and measured dimensions
  const onLayout = useCallback(async (direction) => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(
      nodes,
      edges,
      direction
    );

    // Use nodes with accurate dimensions from measurement-based layout
    const nodesWithDimensions = layoutedNodes;

    setNodes([...nodesWithDimensions]);

    // Apply advanced routing with debouncing
    if (routingTimeoutRef.current) {
      clearTimeout(routingTimeoutRef.current);
    }
    
    routingTimeoutRef.current = setTimeout(async () => {
      const routedEdges = await processEdgesWithRouting(
        nodesWithDimensions, 
        layoutedEdges, 
        direction
      );
      setEdges(routedEdges);
      
      setTimeout(() => {
        fitView({ 
          padding: 0.1, 
          duration: 500, 
          includeHiddenNodes: true,
          minZoom: 0.1,
          maxZoom: 1
        });
      }, 100);
    }, 100);
  }, [nodes, edges, setNodes, setEdges, fitView, processEdgesWithRouting]);

  // Memoized node and edge types
  const memoizedNodeTypes = useMemo(() => nodeTypes, []);
  // The parser only ever emits the advanced* edge types (see getEdgeType), so the
  // previous default/error/condition/routed* aliases were dead entries.
  const memoizedEdgeTypes = useMemo(() => advancedEdgeTypes, []);

  if (isLoading) {
    return (
      <div style={{
        width: '100%',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Loading contact flow diagram...
          </div>
          {isRouting && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              Computing optimal edge routes...
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffebee',
        border: '1px solid #ef5350',
        borderRadius: '4px'
      }}>
        <div style={{ textAlign: 'center', color: '#c62828' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            ⚠️ Error Loading Diagram
          </div>
          <div style={{ fontSize: '14px' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={memoizedNodeTypes}
        edgeTypes={memoizedEdgeTypes}
        fitView={fitViewOnLoad}
        attributionPosition="top-right"
        minZoom={0.05}
        maxZoom={4}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        fitViewOptions={{
          padding: 0.1,
          includeHiddenNodes: true,
          minZoom: 0.05,
          maxZoom: 1
        }}
      >
        {showControls && <Controls />}
        
        {showMiniMap && (
          <MiniMap 
            style={{
              height: 120,
              background: '#f8f9fa',
              border: '1px solid #dee2e6'
            }}
            zoomable
            pannable
          />
        )}
        
        <Background color="#aaa" gap={16} />
        
        {/* Enhanced Flow Information Panel */}
        <Panel position="top-left">
          <div style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '12px',
            fontSize: '12px',
            maxWidth: '320px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2c3e50' }}>
              {contactFlow.Name || 'Contact Flow'}
            </div>
            
            {flowSummary && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                <div>Actions: {flowSummary.actionCount} (+1 Entry)</div>
                <div>Transitions: {flowSummary.transitionCount}</div>
                <div>Version: {flowSummary.version}</div>
                {contactFlow.ContactFlowType && (
                  <div>Type: {contactFlow.ContactFlowType}</div>
                )}
                
                {contactFlow.Description && (
                  <div style={{ marginTop: '8px', fontSize: '10px', fontStyle: 'italic' }}>
                    {contactFlow.Description}
                  </div>
                )}
              </div>
            )}
          </div>
        </Panel>

        {/* AI Flow Summary Panel - Only shown during exports */}
        {showAISummary && contactFlow.summary && (
          <Panel position="top-left" style={{ top: '200px' }}>
            <div style={{
              background: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '12px',
              fontSize: '10px',
              maxWidth: '320px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2c3e50', fontSize: '11px' }}>
                Flow Summary
              </div>
              <div>
                {Array.isArray(contactFlow.summary)
                  ? contactFlow.summary.join('\n')
                  : contactFlow.summary}
              </div>
            </div>
          </Panel>
        )}

        {/* Simplified Control Panel */}
        <Panel position="top-right">
          <div style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '8px',
            display: 'flex',
            gap: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <button
              onClick={() => onLayout('TB')}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                border: '1px solid #dee2e6',
                borderRadius: '3px',
                background: layoutDirection === 'TB' ? '#e3f2fd' : 'white',
                cursor: 'pointer'
              }}
              title="Vertical Layout"
            >
              ↕️
            </button>
            <button
              onClick={() => onLayout('LR')}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                border: '1px solid #dee2e6',
                borderRadius: '3px',
                background: layoutDirection === 'LR' ? '#e3f2fd' : 'white',
                cursor: 'pointer'
              }}
              title="Horizontal Layout"
            >
              ↔️
            </button>
            <button
              onClick={() => fitView({ padding: 0.1, duration: 500 })}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                border: '1px solid #dee2e6',
                borderRadius: '3px',
                background: 'white',
                cursor: 'pointer'
              }}
              title="Fit to View"
            >
              🔍
            </button>
          </div>
        </Panel>

        {/* Enhanced Legend Panel */}
        <Panel position="bottom-left">
          <div style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '8px',
            fontSize: '10px',
            maxWidth: '280px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Node Types</div>
            
            {/* Entry Point */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#f5f5f5',
                border: '2px solid #9e9e9e',
                marginRight: '6px'
              }}></div>
              Entry Point
            </div>
            
            {/* Start/End Nodes */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#e8f5e8',
                border: '1px solid #4caf50',
                marginRight: '6px'
              }}></div>
              Start/End Actions
            </div>
            
            {/* Communication (Blue) - MessageParticipant */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '8px',
                background: '#E3F2FD',
                border: '1px solid #2196F3',
                marginRight: '6px',
                borderRadius: '2px'
              }}></div>
              Messages/Communication
            </div>
            
            {/* Input (Purple) - GetParticipantInput */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '8px',
                background: '#F3E5F5',
                border: '1px solid #9C27B0',
                marginRight: '6px',
                borderRadius: '2px'
              }}></div>
              Input Collection
            </div>
            
            {/* Integration (Orange) - Lambda */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '8px',
                background: '#FFF3E0',
                border: '1px solid #FF9800',
                marginRight: '6px',
                borderRadius: '2px'
              }}></div>
              Integration/Lambda
            </div>
            
            {/* Routing (Green) - Queues, Transfers */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '8px',
                background: '#E8F5E8',
                border: '1px solid #4CAF50',
                marginRight: '6px',
                borderRadius: '2px'
              }}></div>
              Routing/Queues
            </div>
            
            {/* Decision/Compare Nodes */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                background: '#FFF3E0',
                border: '1px solid #ff9800',
                transform: 'rotate(45deg)',
                marginRight: '10px'
              }}></div>
              Decision/Compare
            </div>
            
            {/* Data Operations (Grey) */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div style={{
                width: '12px',
                height: '8px',
                background: '#ECEFF1',
                border: '1px solid #607D8B',
                marginRight: '6px',
                borderRadius: '2px'
              }}></div>
              Data/Attributes
            </div>
            
            <div style={{ borderTop: '1px solid #eee', paddingTop: '4px', marginTop: '4px', fontSize: '9px', color: '#666' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Edge Types</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{
                  width: '16px',
                  height: '1px',
                  background: '#4CAF50',
                  marginRight: '6px'
                }}></div>
                Success Path
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{
                  width: '16px',
                  height: '1px',
                  background: '#f44336',
                  marginRight: '6px',
                  borderTop: '1px dashed #f44336'
                }}></div>
                Error Path
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{
                  width: '16px',
                  height: '1px',
                  background: '#2196f3',
                  marginRight: '6px'
                }}></div>
                Condition Path
              </div>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
});

// Wrapper component with ReactFlowProvider
export const ContactFlowDiagram = forwardRef((props, ref) => {
  return (
    <ReactFlowProvider>
      <ContactFlowDiagramInner {...props} ref={ref} />
    </ReactFlowProvider>
  );
});